import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../../../services/apiService";
import "./auth.css";


export default function ResetPassword() {
  const navigate      = useNavigate();
  const [params]      = useSearchParams();

  const [phone, setPhone]           = useState("");
  const [password, setPassword]     = useState("");
  const [confirm, setConfirm]       = useState("");
  const [showPass, setShowPass]     = useState(false);
  const [showConf, setShowConf]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [err, setErr]               = useState("");
  const [success, setSuccess]       = useState(false);

  // Pre-fill phone from query param (passed from ForgotPassword after OTP verify)
  useEffect(() => {
    const p = params.get("phone");
    if (p) setPhone(p);
  }, [params]);

  const handleReset = async (e) => {
    e?.preventDefault?.();
    setErr("");

    if (!phone || phone.length < 10) {
      setErr("Phone number is missing. Please go back and verify again.");
      return;
    }
    if (!password || password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      // Try apiService first, fallback to direct fetch
      let data;
      try {
        data = await apiService.resetPasswordByPhone(phone, password);
      } catch {
        // In ResetPassword.jsx, change the fetch line to:
const res = await fetch(`https://xiadot.com/vendor_api/reset-password.php`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ phone, password }),
});
        data = await res.json();
      }

      if (data?.success || data?.message?.toLowerCase().includes("success")) {
        setSuccess(true);
        setTimeout(() => navigate("/login"), 2500);
      } else {
        setErr(data?.message || "Failed to reset password. Please try again.");
      }
    } catch {
      setErr("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vl-page">

      {/* ── LEFT branding panel ── */}
      <div className="vl-left">
        <div className="vl-left-glow-1" />
        <div className="vl-left-glow-2" />

        <div className="vl-left-top">
          <div className="vl-logo">
            <div className="vl-logo-icon">
              <img src={process.env.PUBLIC_URL + "/logo.png"} alt="Yuro Shopy" />
            </div>
            <span className="vl-logo-name">YURO SHOPY</span>
          </div>
        </div>

        <div className="vl-left-mid">
          <div className="vl-left-tag">Vendor Portal</div>
          <h1 className="vl-left-h1">Set a new<br /><em>password</em></h1>
          <p className="vl-left-p">
            Choose a strong password to keep your vendor account safe and secure.
          </p>

          <div className="vl-stat-row">
            <div className="vl-stat">
              <span className="vl-stat-num">12K+</span>
              <span className="vl-stat-lbl">Active Vendors</span>
            </div>
            <div className="vl-stat-div" />
            <div className="vl-stat">
              <span className="vl-stat-num">98%</span>
              <span className="vl-stat-lbl">Uptime SLA</span>
            </div>
            <div className="vl-stat-div" />
            <div className="vl-stat">
              <span className="vl-stat-num">4.9★</span>
              <span className="vl-stat-lbl">Satisfaction</span>
            </div>
          </div>
        </div>

        <div className="vl-left-bot">
          {["Real-time sales analytics", "Inventory & order management", "Instant payout tracking"].map((f, i) => (
            <div className="vl-feat" key={i}>
              <div className="vl-feat-check">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT: Form panel ── */}
      <div className="vl-right">

        {/* Mobile-only top branding */}
        <div className="vl-mob-brand">
          <div className="vl-logo-icon" style={{ width: 34, height: 34, borderRadius: 9 }}>
            <img src={process.env.PUBLIC_URL + "/logo.png"} alt="Yuro Shopy" />
          </div>
          <span className="vl-logo-name">YURO SHOPY</span>
        </div>

        {/* Mobile hero */}
        <div className="vl-mob-hero">
          <div className="vl-left-tag" style={{ marginBottom: 12 }}>Vendor Portal</div>
          <h2 className="vl-mob-h2">Set a new password</h2>
          <p className="vl-mob-p">Choose a strong password for your account.</p>
        </div>

        {/* Form card */}
        <div className="vl-card">

          {/* ── Success state ── */}
          {success ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px"
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="vl-card-label" style={{ marginBottom: 8 }}>Success!</div>
              <h2 className="vl-card-title">Password Updated</h2>
              <p className="vl-card-sub">
                Your password has been reset successfully. Redirecting to login…
              </p>
              <div style={{
                marginTop: 20, height: 4, borderRadius: 4,
                background: "#e2e8f0", overflow: "hidden"
              }}>
                <div style={{
                  height: "100%", background: "#6366f1",
                  animation: "progressBar 2.5s linear forwards"
                }} />
              </div>
              <style>{`
                @keyframes progressBar { from { width: 0% } to { width: 100% } }
              `}</style>
            </div>
          ) : (
            <>
              <div className="vl-card-label">New Password</div>
              <h2 className="vl-card-title">Reset your password</h2>
              <p className="vl-card-sub">
                {phone ? `Setting new password for +91 ${phone}` : "Enter your new password below"}
              </p>

              {/* Error */}
              {err && (
                <div style={{
                  background: "#fef2f2", border: "1px solid #fecaca",
                  borderRadius: 10, padding: "10px 14px", marginBottom: 16,
                  color: "#dc2626", fontSize: 13,
                  display: "flex", alignItems: "center", gap: 8
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {err}
                </div>
              )}

              <form onSubmit={handleReset}>

                {/* New Password */}
                <div className="vl-field">
                  <label className="vl-lbl">New Password</label>
                  <div className="vl-iw">
                    <span className="vl-ii">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                      </svg>
                    </span>
                    <input
                      className="vl-inp vl-inp-p"
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 6 characters"
                      required
                    />
                    <button type="button" className="vl-eye" onClick={() => setShowPass(s => !s)}>
                      {showPass ? (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Password strength indicator */}
                  {password && (
                    <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} style={{
                          flex: 1, height: 3, borderRadius: 3,
                          background: password.length >= i * 2
                            ? i <= 1 ? "#ef4444"
                              : i <= 2 ? "#f59e0b"
                                : i <= 3 ? "#3b82f6" : "#10b981"
                            : "#e2e8f0",
                          transition: "background 0.2s"
                        }} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="vl-field">
                  <label className="vl-lbl">Confirm Password</label>
                  <div className="vl-iw">
                    <span className="vl-ii">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <input
                      className="vl-inp vl-inp-p"
                      type={showConf ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Re-enter password"
                      required
                    />
                    <button type="button" className="vl-eye" onClick={() => setShowConf(s => !s)}>
                      {showConf ? (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {/* Match indicator */}
                  {confirm && (
                    <p style={{
                      fontSize: 12, marginTop: 4,
                      color: password === confirm ? "#10b981" : "#ef4444"
                    }}>
                      {password === confirm ? "✓ Passwords match" : "✗ Passwords do not match"}
                    </p>
                  )}
                </div>

                <button className="vl-btn" type="submit" disabled={loading}>
                  {loading ? (
                    <><span className="vl-spin" />Updating Password…</>
                  ) : (
                    <>Update Password
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          <div className="vl-footer">
            Remember your password? <Link to="/login">Sign in</Link>
          </div>
        </div>

        {/* Mobile feature pills */}
        <div className="vl-mob-feats">
          {["Sales analytics", "Order management", "Payout tracking"].map((f, i) => (
            <div className="vl-mob-pill" key={i}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {f}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}